import type { StatusTone } from '@beonit/adaptive-raindrop';
import type {
  GestureMatrix,
  GestureMatrixCell,
  GestureMatrixKind,
  GestureMatrixVideo,
} from '../model.js';
import { CONTROL_KIND_TITLES } from './control-groups.js';
import { gestureNameLabel } from './gesture-catalogue.js';

/**
 * Where everything goes on a control group's gesture map: videos down, gestures across, and in
 * each cell what was labelled, what of it the detector found and what it found that nobody
 * labelled (product owner, 2026-10-06).
 *
 * `GestureMatrix` says what the cells hold, as upstream counted it; this says where each is drawn
 * and how it reads. Kept apart and pure, like `rules-map-layout.ts`, so the geometry, the tones and
 * what a hover lights are tested like the rest, and the component only paints coordinates.
 *
 * ## A cell reads in one glance, and in words on hover
 *
 * Each cell is matched over labelled («2/3») and, after it, what was found with no label («+1»):
 * the three counts upstream gives, and nothing recomputed — a percentage worked out here would be a
 * second truth beside the card's. Its tone says which of the three things happened:
 *
 * - **hit** — every label found and nothing extra;
 * - **miss** — some label not found. It outranks an extra: detection is the control's first
 *   target, and a cell that missed and over-detected is first of all one that missed;
 * - **false positive** — every label found, or none to find, and something found that nobody
 *   labelled;
 * - **empty** — no label and no detection of that gesture in the video, drawn muted.
 *
 * ## Every video is a row
 *
 * Upstream lists every video of the group, the ones its figures leave out too, so the map also says
 * which ones and why. They go under the evaluated ones, in a section of their own, with the reason
 * across the row: no cell to draw is not a cell that is empty.
 *
 * ## Hovering isolates
 *
 * A row lights its cells and the gestures it has something in; a column lights its cells and the
 * videos it has something in. Everything else dims, as on the rules map.
 */

export type MatrixCellTone = 'hit' | 'miss' | 'false_positive' | 'empty';

/** Each tone by the design system's status tones: the component paints them, never a colour. */
export const MATRIX_CELL_TONES: Readonly<Record<MatrixCellTone, StatusTone>> = Object.freeze({
  hit: 'success',
  miss: 'danger',
  false_positive: 'warning',
  empty: 'neutral',
});

/** The legend, in the order a cell is read. */
export const MATRIX_LEGEND: ReadonlyArray<{
  tone: MatrixCellTone;
  label: string;
}> = Object.freeze([
  { tone: 'hit', label: 'Todas detectadas' },
  { tone: 'miss', label: 'Etiquetas sin detectar' },
  { tone: 'false_positive', label: 'Detecciones de más' },
  { tone: 'empty', label: 'Nada de ese gesto' },
]);

export function cellTone(
  cell: Pick<GestureMatrixCell, 'labels' | 'labelsDetected' | 'falsePositives'>,
): MatrixCellTone {
  if (cell.labelsDetected < cell.labels) return 'miss';
  if (cell.falsePositives > 0) return 'false_positive';

  return cell.labels > 0 ? 'hit' : 'empty';
}

// ── Words ────────────────────────────────────────────────────────────────────

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

const NOT_EVALUATED: Readonly<Record<string, { label: string; detail: string }>> = Object.freeze({
  labelling_incomplete: {
    label: 'Etiquetado incompleto',
    detail: 'Su etiquetado de gestos no está marcado como completo.',
  },
  no_result: {
    label: 'Sin resultado',
    detail: 'No tiene un análisis terminado en esta ejecución.',
  },
  // Neutral: a Gestos run that skipped the detector did not read the labels either.
  gestures_not_executed: {
    label: 'Sin detector de gestos',
    detail: 'Su análisis no ejecutó el detector de gestos.',
  },
});

/** Semántica's runs read the labelled gestures instead of running the detector: there, it says so. */
const READ_THE_LABELS =
  'Su análisis leyó los gestos etiquetados: no ejecutó el detector de gestos.';

/** Why a video was left out, in one word or three; upstream's spelling for a reason it adds later. */
export function notEvaluatedLabel(reason: string | null): string {
  if (reason === null) return 'Sin evaluar';

  return NOT_EVALUATED[reason]?.label ?? `Sin evaluar («${reason}»)`;
}

/**
 * The same, in a sentence: what the row says across the cells it does not have. The map's kind says
 * why the detector did not run when only one kind can know it.
 */
export function notEvaluatedDetail(
  reason: string | null,
  kind: GestureMatrixKind | null = null,
): string {
  if (reason === null) return 'Sus cifras no cuentan en esta ejecución.';
  if (reason === 'gestures_not_executed' && kind === 'semantics') return READ_THE_LABELS;

  return NOT_EVALUATED[reason]?.detail ?? 'Sus cifras no cuentan en esta ejecución.';
}

/** A video's name, or its number when upstream no longer finds it to name it. */
export function matrixVideoName(video: Pick<GestureMatrixVideo, 'name' | 'sourceId'>): string {
  return video.name ?? `Vídeo #${video.sourceId}`;
}

/** «Correcto», «Incorrecto», or why it was left out: the row's badge. */
export function rowStatus(video: GestureMatrixVideo): {
  tone: StatusTone;
  label: string;
} {
  if (!video.evaluated) {
    return {
      tone: 'neutral',
      label: notEvaluatedLabel(video.notEvaluatedReason),
    };
  }
  if (video.correct === true) return { tone: 'success', label: 'Correcto' };
  if (video.correct === false) return { tone: 'warning', label: 'Incorrecto' };

  return { tone: 'neutral', label: 'Sin veredicto' };
}

/** «9 de 10 detectadas · 2 de más»: the row's own counts; null on a video left out. */
export function rowSummary(video: GestureMatrixVideo): string | null {
  if (!video.evaluated || video.labels === null || video.labelsDetected === null) return null;

  const extra = video.falsePositives ?? 0;

  return [
    `${video.labelsDetected} de ${plural(video.labels, 'detectada', 'detectadas')}`,
    extra === 0 ? 'ninguna de más' : `${extra} de más`,
  ].join(' · ');
}

/** «3 etiquetas, 2 detectadas, 1 detección de más»: a cell in words, on its hover and its panel. */
export function cellWords(cell: GestureMatrixCell): string {
  const tone = cellTone(cell);

  if (tone === 'empty') return 'sin etiquetas ni detecciones';

  return [
    plural(cell.labels, 'etiqueta', 'etiquetas'),
    plural(cell.labelsDetected, 'detectada', 'detectadas'),
    cell.falsePositives === 0
      ? 'ninguna detección de más'
      : plural(cell.falsePositives, 'detección de más', 'detecciones de más'),
  ].join(', ');
}

// ── The layout ───────────────────────────────────────────────────────────────

/**
 * - `name`: upstream's order, by name then id;
 * - `worst`: the incorrect videos first, then the ones that missed or over-detected most — where to
 *   look first when a figure is off target.
 */
export type MatrixOrder = 'name' | 'worst';

export const MATRIX_ORDER_LABELS: Readonly<Record<MatrixOrder, string>> = Object.freeze({
  name: 'Por nombre',
  worst: 'Peores primero',
});

export interface MatrixInput {
  matrix: GestureMatrix;
  order?: MatrixOrder;
  /** What a gesture is called; the screen's own list when the catalogue is not there. */
  nameOf?: (gesture: string) => string;
  /**
   * The columns, when the screen picks them: a group of the catalogue, its gestures with nothing
   * labelled or found included. Upstream's columns when not given.
   */
  gestures?: readonly string[];
  /** The name a cell's gesture goes by in `gestures`: an old id is matched to its current name. */
  canonical?: (gesture: string) => string;
  rowHeaderWidth?: number;
  cellWidth?: number;
  rowHeight?: number;
  /** Room for the gestures' names over their columns, slanted. */
  headerHeight?: number;
  sectionHeight?: number;
  /** How far the last column's slanted name leans out past the grid. */
  labelOverhang?: number;
  /** The least room a left-out video's sentence gets across where its cells would be. */
  leftOutWidth?: number;
}

export interface MatrixColumn {
  id: string;
  gesture: string;
  label: string;
  x: number;
  width: number;
  /** Over the evaluated videos: what the column adds up to. */
  labels: number;
  labelsDetected: number;
  falsePositives: number;
  /** «Cejas bajadas: 6 de 8 etiquetas detectadas · 2 detecciones de más». */
  title: string;
}

export type MatrixSectionKey = 'evaluated' | 'left_out';

export interface MatrixSection {
  key: MatrixSectionKey;
  /** «Evaluados · 2», «Sin evaluar · 3». */
  label: string;
  y: number;
  height: number;
  count: number;
}

export interface MatrixRow {
  id: string;
  sourceId: number;
  name: string;
  /** Deleted from the dataset after the execution: shown struck through, figures intact. */
  deleted: boolean;
  section: MatrixSectionKey;
  y: number;
  height: number;
  status: { tone: StatusTone; label: string };
  summary: string | null;
  /** On a video left out, why, across where its cells would be; null on one evaluated. */
  leftOut: string | null;
}

export interface MatrixCellBox {
  id: string;
  rowId: string;
  columnId: string;
  gesture: string;
  sourceId: number;
  x: number;
  y: number;
  width: number;
  height: number;
  tone: MatrixCellTone;
  /** «2/3»: matched over labelled; null when nothing was labelled. */
  ratio: string | null;
  /** «+1»: found with no label; null when nothing was. */
  extra: string | null;
  /** The cell in words, for its tooltip and a screen reader. */
  title: string;
}

export interface MatrixLayout {
  /** The grid: the videos' names and the columns. */
  width: number;
  /**
   * What the board takes, wider than the grid: the slanted names of the last columns lean out past
   * it, and a left-out video's sentence needs its room however few the columns. The section rules
   * and those sentences span it.
   */
  boardWidth: number;
  height: number;
  headerHeight: number;
  rowHeaderWidth: number;
  columns: MatrixColumn[];
  sections: MatrixSection[];
  rows: MatrixRow[];
  cells: MatrixCellBox[];
}

const EMPTY_CELL: Omit<GestureMatrixCell, 'gesture'> = Object.freeze({
  labels: 0,
  labelsDetected: 0,
  detections: 0,
  falsePositives: 0,
});

/** How far off a video is: what it missed and what it found extra, counted together. */
function offBy(video: GestureMatrixVideo): number {
  return (video.labels ?? 0) - (video.labelsDetected ?? 0) + (video.falsePositives ?? 0);
}

/** The evaluated videos in the order asked; upstream's order breaks every tie. */
export function sortMatrixVideos(
  videos: readonly GestureMatrixVideo[],
  order: MatrixOrder,
): GestureMatrixVideo[] {
  const indexed = videos.map((video, index) => ({ video, index }));

  if (order === 'worst') {
    const rank = (video: GestureMatrixVideo) => (video.correct === false ? 0 : 1);

    indexed.sort(
      (a, b) =>
        rank(a.video) - rank(b.video) || offBy(b.video) - offBy(a.video) || a.index - b.index,
    );
  }

  return indexed.map(({ video }) => video);
}

function columnId(gesture: string): string {
  return `c:${gesture}`;
}

function rowId(sourceId: number): string {
  return `v:${sourceId}`;
}

export function layoutMatrix(input: MatrixInput): MatrixLayout {
  const { matrix } = input;
  const nameOf = input.nameOf ?? gestureNameLabel;
  const canonical = input.canonical ?? ((gesture: string) => gesture);
  const cellOf = (video: GestureMatrixVideo, gesture: string) =>
    video.cells.find((cell) => canonical(cell.gesture) === gesture);
  const rowHeaderWidth = input.rowHeaderWidth ?? 240;
  const cellWidth = input.cellWidth ?? 56;
  const rowHeight = input.rowHeight ?? 36;
  const headerHeight = input.headerHeight ?? 132;
  const sectionHeight = input.sectionHeight ?? 28;
  const labelOverhang = input.labelOverhang ?? 112;
  const leftOutWidth = input.leftOutWidth ?? 360;

  const evaluated = sortMatrixVideos(
    matrix.videos.filter((video) => video.evaluated),
    input.order ?? 'name',
  );
  const leftOut = matrix.videos.filter((video) => !video.evaluated);

  // ── Columns, the screen's or upstream's order: the most labelled first ──
  const columns: MatrixColumn[] = (input.gestures ?? matrix.gestures).map((gesture, index) => {
    const cells = evaluated.flatMap((video) => {
      const cell = cellOf(video, gesture);

      return cell ? [cell] : [];
    });
    const labels = cells.reduce((sum, cell) => sum + cell.labels, 0);
    const labelsDetected = cells.reduce((sum, cell) => sum + cell.labelsDetected, 0);
    const falsePositives = cells.reduce((sum, cell) => sum + cell.falsePositives, 0);
    const label = nameOf(gesture);

    return {
      id: columnId(gesture),
      gesture,
      label,
      x: rowHeaderWidth + index * cellWidth,
      width: cellWidth,
      labels,
      labelsDetected,
      falsePositives,
      title: `${label}: ${labelsDetected} de ${plural(labels, 'etiqueta detectada', 'etiquetas detectadas')} · ${
        falsePositives === 0
          ? 'ninguna detección de más'
          : plural(falsePositives, 'detección de más', 'detecciones de más')
      }`,
    };
  });

  // ── Rows, by section ──
  const sections: MatrixSection[] = [];
  const rows: MatrixRow[] = [];
  const cells: MatrixCellBox[] = [];
  let cursor = headerHeight;

  const place = (key: MatrixSectionKey, label: string, videos: GestureMatrixVideo[]) => {
    if (videos.length === 0) return;

    sections.push({
      key,
      label: `${label} · ${videos.length}`,
      y: cursor,
      height: sectionHeight,
      count: videos.length,
    });
    cursor += sectionHeight;

    for (const video of videos) {
      const id = rowId(video.sourceId);
      const name = matrixVideoName(video);

      rows.push({
        id,
        sourceId: video.sourceId,
        name,
        deleted: video.sourceDeleted === true,
        section: key,
        y: cursor,
        height: rowHeight,
        status: rowStatus(video),
        summary: rowSummary(video),
        leftOut: video.evaluated ? null : notEvaluatedDetail(video.notEvaluatedReason, matrix.kind),
      });

      if (video.evaluated) {
        for (const column of columns) {
          const cell = cellOf(video, column.gesture) ?? {
            gesture: column.gesture,
            ...EMPTY_CELL,
          };
          const tone = cellTone(cell);

          cells.push({
            id: `${id}|${column.id}`,
            rowId: id,
            columnId: column.id,
            gesture: column.gesture,
            sourceId: video.sourceId,
            x: column.x,
            y: cursor,
            width: cellWidth,
            height: rowHeight,
            tone,
            ratio: cell.labels > 0 ? `${cell.labelsDetected}/${cell.labels}` : null,
            extra: cell.falsePositives > 0 ? `+${cell.falsePositives}` : null,
            title: `${column.label} · ${name}: ${cellWords(cell)}`,
          });
        }
      }

      cursor += rowHeight;
    }
  };

  place('evaluated', 'Evaluados', evaluated);
  place('left_out', 'Sin evaluar', leftOut);

  const width = rowHeaderWidth + columns.length * cellWidth;

  return {
    width,
    boardWidth: Math.max(width + labelOverhang, rowHeaderWidth + leftOutWidth),
    height: cursor,
    headerHeight,
    rowHeaderWidth,
    columns,
    sections,
    rows,
    cells,
  };
}

// ── Isolating a row or a column ──────────────────────────────────────────────

export type MatrixFocus = { kind: 'row' | 'column'; id: string } | null;

export interface MatrixIsolation {
  rows: Set<string>;
  columns: Set<string>;
  cells: Set<string>;
}

/**
 * What stays lit while a row or a column is hovered: its cells, and across from them the gestures
 * (or the videos) it has something in. Null lights everything.
 */
export function isolateMatrix(layout: MatrixLayout, focus: MatrixFocus): MatrixIsolation | null {
  if (!focus) return null;

  const isolation: MatrixIsolation = {
    rows: new Set(),
    columns: new Set(),
    cells: new Set(),
  };

  if (focus.kind === 'row') isolation.rows.add(focus.id);
  else isolation.columns.add(focus.id);

  for (const cell of layout.cells) {
    const mine = focus.kind === 'row' ? cell.rowId === focus.id : cell.columnId === focus.id;

    if (!mine) continue;

    isolation.cells.add(cell.id);
    if (cell.tone === 'empty') continue;

    if (focus.kind === 'row') isolation.columns.add(cell.columnId);
    else isolation.rows.add(cell.rowId);
  }

  return isolation;
}

// ── When there is no map to draw ─────────────────────────────────────────────

export interface MatrixNotice {
  title: string;
  description: string;
}

/**
 * What the map says instead of drawing itself, or null when it draws: no execution yet, no videos,
 * or a Semántica execution — whose runs read the labelled gestures instead of running the detector,
 * so there are no detections to hold against the labels.
 */
export function matrixNotice(matrix: GestureMatrix): MatrixNotice | null {
  if (matrix.batch === null) {
    return {
      title: 'Sin ejecución',
      description: `${CONTROL_KIND_TITLES[matrix.kind]} no se ha ejecutado todavía: el mapa aparece con su primera ejecución.`,
    };
  }

  if (matrix.videos.length === 0) {
    return {
      title: 'Sin vídeos',
      description: 'Esta ejecución no tiene vídeos que mostrar.',
    };
  }

  if (matrix.kind === 'semantics' && !matrix.videos.some((video) => video.evaluated)) {
    return {
      title: 'Semántica no ejecuta el detector de gestos',
      description:
        'Sus ejecuciones leen los gestos etiquetados a mano, así que no hay detecciones que comparar con las etiquetas. La detección se mide en el control Gestos.',
    };
  }

  return null;
}
